import { NODE_METRICS } from '@citilight-monitor/contracts';
import { useMemo, useState } from 'react';
import { Card, EmptyState } from '../components/atoms';
import { SectionHeader } from '../components/molecules';
import { ClientsTable, MultiServerChart, RangePicker, type ServerSeries } from '../components/organisms';
import { PageTemplate } from '../components/templates';
import { cn } from '../lib/cn';
import { useFleetSeries, useOverview } from '../lib/api';
import { chartPoints, isServer, metricStatus, metricView, seriesColor } from '../lib/metrics';
import { RANGE_PRESETS, useRangeSelection } from '../lib/time';

const PANELS = [
  { key: 'cpu_percent', label: 'CPU' },
  { key: 'memory_percent', label: 'Memory' },
  { key: 'disk_percent', label: 'Disk' },
] as const;

export function ServersPage() {
  const { data, error, isPending } = useOverview();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [range, setRange] = useRangeSelection();

  const servers = useMemo(() => (data?.clients ?? []).filter((c) => isServer(c.client.metrics)), [data]);
  const sortedIds = useMemo(() => [...servers].map((s) => s.client.id).sort(), [servers]);
  const colorById = useMemo(() => new Map(sortedIds.map((id, i) => [id, seriesColor(i)])), [sortedIds]);

  const ids = servers.map((s) => s.client.id);
  const fleetSeries = useFleetSeries(ids, range);
  const rangeLabel = (RANGE_PRESETS.find((p) => p.id === (range.kind === 'preset' ? range.preset : undefined))?.label ?? 'the selected range').toLowerCase();

  if (isPending || error) {
    return (
      <PageTemplate title="Servers">
        <Card raised>
          <EmptyState>{error ? `Could not load servers: ${error.message}` : 'Loading servers…'}</EmptyState>
        </Card>
      </PageTemplate>
    );
  }

  const seriesByClient = new Map(ids.map((id, i) => [id, fleetSeries[i]?.data]));
  const shown = servers.filter((s) => !hidden.has(s.client.id));
  const anyRange = shown.map((s) => seriesByClient.get(s.client.id)).find((sd) => sd !== undefined);

  function toggle(id: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <PageTemplate
      title="Servers"
      meta={`${servers.length} servers · polled every ${data.pollIntervalMinutes} min`}
      actions={<RangePicker range={range} onChange={setRange} />}
    >
      {servers.length === 0 ? (
        <Card raised>
          <EmptyState>No servers are configured with node_exporter yet.</EmptyState>
        </Card>
      ) : (
        <>
          <section className="flex flex-col gap-3" aria-label="Server filter">
            <SectionHeader title="Compare" description="Hide a server to drop it from the charts and table below." onBand />
            <div role="group" aria-label="Visible servers" className="flex flex-wrap gap-2">
              {servers.map((s) => {
                const on = !hidden.has(s.client.id);
                return (
                  <button
                    key={s.client.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggle(s.client.id)}
                    className={cn(
                      'inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
                      on ? 'border-transparent bg-surface-sunken text-fg' : 'border-line text-fg-muted hover:text-fg',
                    )}
                  >
                    <span className="size-2 rounded-full" style={{ background: colorById.get(s.client.id), opacity: on ? 1 : 0.35 }} aria-hidden />
                    {s.client.name}
                    <span className="font-mono text-[11px] text-fg-muted">{s.client.ip ?? s.client.id}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {shown.length === 0 ? (
            <Card raised>
              <EmptyState>All servers are hidden. Select one above to see its charts.</EmptyState>
            </Card>
          ) : (
            <div className="flex flex-col gap-5">
              {PANELS.map((panel) => {
                const definition = NODE_METRICS.find((m) => m.key === panel.key)!;
                const view = metricView(definition);
                const series: ServerSeries[] = shown.map((status) => ({
                  id: status.client.id,
                  label: `${status.client.name} · ${status.client.ip ?? status.client.id}`,
                  color: colorById.get(status.client.id)!,
                  points: chartPoints(seriesByClient.get(status.client.id), view),
                  threshold: metricStatus(status, panel.key)?.rule.threshold,
                }));
                const hasData = series.some((s) => s.points.length > 0);

                return (
                  <Card as="section" key={panel.key} className="px-5 pb-4 pt-4" aria-label={`${panel.label} usage across servers`}>
                    <h2 className="mb-3 text-base">{panel.label}</h2>
                    {!hasData ? (
                      <EmptyState height={220}>No data in {rangeLabel}</EmptyState>
                    ) : (
                      <MultiServerChart
                        series={series}
                        fromMs={anyRange ? Date.parse(anyRange.from) : Date.now() - 86_400_000}
                        toMs={anyRange ? Date.parse(anyRange.to) : Date.now()}
                        binMs={anyRange?.binMs ?? 60_000}
                        domain={view.domain ?? [0, 100]}
                        formatValue={view.formatValue}
                        formatTick={view.formatTick}
                        ariaLabel={`${panel.label} usage, ${series.length} servers, ${rangeLabel}`}
                      />
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          <section className="flex flex-col gap-4" aria-label="Server details">
            <SectionHeader title="All servers" count={servers.length} description="Exact current values. Select a row for its full history and alerts." />
            <Card className="overflow-hidden">
              <ClientsTable clients={servers} />
            </Card>
          </section>
        </>
      )}
    </PageTemplate>
  );
}
